import {
  WmOption,
  WmSelect,
  WmToggletip
} from "@watermarkinsights/ripple-react";
import { useContext, useEffect, useReducer } from "react";
import { useIntl } from "react-intl";
import { sendRequest, sendRequestWithPayload } from "@/common/http";
import translate from "@/i18n/translate";
import CourseCompletionRisk from "./CourseCompletionRisk";
import TermPersistenceRisk from "./TermPersistenceRisk";
import "./Sse.scss";
import { formatNumber } from "../../../src/static/js/commons";
import WidgetLoader from "../common/elements/WidgetLoader";
import { getLoaderStatus } from "../common/helper";
import ApiError from "../common/elements/ApiError";
import { WmButton } from "@watermarkinsights/ripple-react";
import { downloadPDF } from "../common/helper";
import { AppContext, AppContextInterface } from "@/contexts/AppContext";

interface Term {
  endDate: string;
  id: string;
  name: string;
  startDate: string;
}

interface TermResultSet {
  showSseWidget: boolean;
  terms: Term[];
}

interface SseTermResultSet {
  averageCourseRisk: string;
  averageTermRisk: string;
  engaged: number;
  highRiskCourseCompletion: number;
  highRiskCourseEngagement: number;
  highRiskTermEngagement: number;
  highRiskTermPersistence: number;
  lowRiskCourseCompletion: number;
  lowRiskCourseEngagement: number;
  lowRiskTermEngagement: number;
  lowRiskTermPersistence: number;
  mediumRiskCourseCompletion: number;
  mediumRiskCourseEngagement: number;
  mediumRiskTermEngagement: number;
  mediumRiskTermPersistence: number;
  notIndicatedRiskCourseCompletion: number;
  notIndicatedRiskCourseEngagement: number;
  notIndicatedRiskTermEngagement: number;
  notIndicatedRiskTermPersistence: number;
  totalEnrollments: number;
}

interface FacultySuccessState {
  showSseWidget: boolean;
  isExportInProgress: boolean;
  terms: Term[];
  averageCourseRisk: string;
  averageTermRisk: string;
  totalEnrollments: number;
  engaged: number;
  highRiskCourseEngagement: number;
  lowRiskCourseEngagement: number;
  mediumRiskCourseEngagement: number;
  notIndicatedRiskCourseEngagement: number;
  highRiskCourseCompletion: number;
  lowRiskCourseCompletion: number;
  mediumRiskCourseCompletion: number;
  notIndicatedRiskCourseCompletion: number;
  highRiskTermPersistence: number;
  mediumRiskTermPersistence: number;
  lowRiskTermPersistence: number;
  notIndicatedRiskTermPersistence: number;
  highRiskTermPersistenceEngagement: number;
  lowRiskTermPersistenceEngagement: number;
  mediumRiskTermPersistenceEngagement: number;
  notIndicatedRiskTermPersistenceEngagement: number;
  loader: boolean;
  termSummaryFailed: boolean;
  selectedTerm: null | Term;
  token: null | string;
}

const initialState = {
  showSseWidget: false,
  isExportInProgress: false,
  terms: [],
  averageCourseRisk: "",
  averageTermRisk: "",
  totalEnrollments: 0,
  engaged: 0,
  highRiskCourseEngagement: 0,
  lowRiskCourseEngagement: 0,
  mediumRiskCourseEngagement: 0,
  notIndicatedRiskCourseEngagement: 0,
  highRiskCourseCompletion: 0,
  lowRiskCourseCompletion: 0,
  mediumRiskCourseCompletion: 0,
  notIndicatedRiskCourseCompletion: 0,
  highRiskTermPersistence: 0,
  mediumRiskTermPersistence: 0,
  lowRiskTermPersistence: 0,
  notIndicatedRiskTermPersistence: 0,
  highRiskTermPersistenceEngagement: 0,
  lowRiskTermPersistenceEngagement: 0,
  mediumRiskTermPersistenceEngagement: 0,
  notIndicatedRiskTermPersistenceEngagement: 0,
  loader: true,
  termSummaryFailed: false,
  selectedTerm: null,
  token: null
};

const setSseTermSummary = (
  sseTermResultSet: SseTermResultSet,
  state: FacultySuccessState
) => {
  if (sseTermResultSet.averageCourseRisk) {
    return {
      ...state,
      averageCourseRisk: sseTermResultSet.averageCourseRisk,
      averageTermRisk: sseTermResultSet.averageTermRisk,
      totalEnrollments: sseTermResultSet.totalEnrollments,
      engaged: sseTermResultSet.engaged,
      highRiskCourseEngagement: sseTermResultSet.highRiskCourseEngagement,
      lowRiskCourseEngagement: sseTermResultSet.lowRiskCourseEngagement,
      mediumRiskCourseEngagement: sseTermResultSet.mediumRiskCourseEngagement,
      notIndicatedRiskCourseEngagement:
        sseTermResultSet.notIndicatedRiskCourseEngagement,
      highRiskCourseCompletion: sseTermResultSet.highRiskCourseCompletion,
      lowRiskCourseCompletion: sseTermResultSet.lowRiskCourseCompletion,
      mediumRiskCourseCompletion: sseTermResultSet.mediumRiskCourseCompletion,
      notIndicatedRiskCourseCompletion:
        sseTermResultSet.notIndicatedRiskCourseCompletion,
      highRiskTermPersistence: sseTermResultSet.highRiskTermPersistence,
      mediumRiskTermPersistence: sseTermResultSet.mediumRiskTermPersistence,
      lowRiskTermPersistence: sseTermResultSet.lowRiskTermPersistence,
      notIndicatedRiskTermPersistence:
        sseTermResultSet.notIndicatedRiskTermPersistence,
      highRiskTermPersistenceEngagement:
        sseTermResultSet.highRiskTermEngagement,
      lowRiskTermPersistenceEngagement: sseTermResultSet.lowRiskTermEngagement,
      mediumRiskTermPersistenceEngagement:
        sseTermResultSet.mediumRiskTermEngagement,
      notIndicatedRiskTermPersistenceEngagement:
        sseTermResultSet.notIndicatedRiskTermEngagement,
      loader: false
    };
  } else {
    return {
      ...state,
      loader: false,
      termSummaryFailed: true
    };
  }
};

interface SseTermSummaryAction {
  type: string;
  termResultSet: TermResultSet;
  sseTermResultSet: SseTermResultSet;
  term: Term;
  isExportInProgress: boolean;
  token: null | string;
}

interface TokenResultSet {
  token: string;
}

interface ExportResultSet {
  pdf_file_bytes: string;
}

const defaultSseTermSummaryAction = {
  type: "",
  termResultSet: {
    showSseWidget: false,
    terms: []
  },
  sseTermResultSet: {
    averageCourseRisk: "",
    averageTermRisk: "",
    engaged: 0,
    highRiskCourseCompletion: 0,
    highRiskCourseEngagement: 0,
    highRiskTermEngagement: 0,
    highRiskTermPersistence: 0,
    lowRiskCourseCompletion: 0,
    lowRiskCourseEngagement: 0,
    lowRiskTermEngagement: 0,
    lowRiskTermPersistence: 0,
    mediumRiskCourseCompletion: 0,
    mediumRiskCourseEngagement: 0,
    mediumRiskTermEngagement: 0,
    mediumRiskTermPersistence: 0,
    notIndicatedRiskCourseCompletion: 0,
    notIndicatedRiskCourseEngagement: 0,
    notIndicatedRiskTermEngagement: 0,
    notIndicatedRiskTermPersistence: 0,
    totalEnrollments: 0
  },
  term: {
    endDate: "",
    id: "",
    name: "",
    startDate: ""
  },
  isExportInProgress: false,
  token: null
};

const stateReducer = (
  state: FacultySuccessState,
  action: SseTermSummaryAction
) => {
  switch (action.type) {
    case "showLoader":
      return { ...state, loader: true, termSummaryFailed: false };
    case "setSseWidgetData":
      return {
        ...state,
        showSseWidget: action.termResultSet.showSseWidget,
        terms: action.termResultSet.terms,
        loader: action.termResultSet.terms.length > 0,
        selectedTerm: action.termResultSet.terms[0]
      };

    case "sseWidgetDataApiFail":
      return {
        ...state,
        showSseWidget: true,
        terms: [],
        loader: false,
        selectedTerm: null
      };
    case "setSseTermSummary":
      return setSseTermSummary(action.sseTermResultSet, state);
    case "sseTermSummaryApiFail":
      return {
        ...state,
        loader: false,
        termSummaryFailed: true
      };
    case "setSelectedTerm":
      return {
        ...state,
        selectedTerm: action.term
      };

    case "setExportInProgress":
      return {
        ...state,
        isExportInProgress: action.isExportInProgress
      };
    case "setToken":
      return { ...state, token: action.token };

    default:
      return state;
  }
};

export default function Sse() {
  const [state, dispatch] = useReducer(stateReducer, initialState);
  const { institution } = useContext<AppContextInterface>(AppContext);

  useEffect(() => {
    const applyResult = (result: TermResultSet) => {
      if (Array.isArray(result.terms) && result.terms.length > 0) {
        const term = result.terms[0];
        fetchTermSummary(term.id);
      }

      dispatch({
        ...defaultSseTermSummaryAction,
        type: "setSseWidgetData",
        termResultSet: result
      });
    };

    const onError = () => {
      dispatch({
        ...defaultSseTermSummaryAction,
        type: "sseWidgetDataApiFail"
      });
    };

    sendRequest({ uri: "/sse/terms", camelize: true }, applyResult, onError);
  }, []);

  useEffect(() => {
    const liveRegion = document.querySelector(".sse-loader-status");

    getLoaderStatus(state.loader, liveRegion);
  }, [state.loader]);

  useEffect(() => {
    if (state.token && state.isExportInProgress) {
      exportToPdf();
    }
  }, [state.isExportInProgress, state.token]);
  useEffect(() => {
    if (state.isExportInProgress) {
      sendRequest(
        {
          uri: "/export/token",
          camelize: true
        },
        (result: TokenResultSet) => {
          dispatch({
            ...defaultSseTermSummaryAction,
            type: "setToken",
            token: result.token
          });
        }
      );
    }
  }, [state.isExportInProgress]);

  const fetchTermSummary = (termId: string) => {
    state.terms.forEach((term: Term) => {
      if (term.id === termId) {
        dispatch({
          ...defaultSseTermSummaryAction,
          type: "setSelectedTerm",
          term: term
        });
      }
    });
    const applyResult = (result: SseTermResultSet) => {
      dispatch({
        ...defaultSseTermSummaryAction,
        type: "setSseTermSummary",
        sseTermResultSet: result
      });
    };

    const onError = () => {
      dispatch({
        ...defaultSseTermSummaryAction,
        type: "sseTermSummaryApiFail"
      });
    };

    sendRequest(
      { uri: `/sse/risk_summary/term/${termId}`, camelize: true },
      applyResult,
      onError
    );
    dispatch({ ...defaultSseTermSummaryAction, type: "showLoader" });
  };

  const predictedRiskTooltipText = useIntl().formatMessage({
    id: "app.sse.avgPredictedRiskDesc"
  });

  const studetOutreachTooltipText = useIntl().formatMessage({
    id: "app.sse.studentOutreachDesc"
  });
  const avgPredictedRiskInfoLabel = useIntl().formatMessage({
    id: "app.sse.avgPredictedRiskInfoLabel"
  });
  const studentOutreachInfoLabel = useIntl().formatMessage({
    id: "app.sse.studentOutreachInfoLabel"
  });

  const termLabel = useIntl().formatMessage({
    id: "app.sse.termLabel"
  });

  const TeamOverviewBody = () => {
    return (
      <div className="body">
        <div className="wm-grid">
          <div className="term-overview-main wm-grid-row">
            <div className="enrollment wm-grid-col-2 wm-grid-col-lg-2 wm-grid-col-md-4 wm-grid-col-sm-8">
              <div className="titles">
                {translate("app.sse.enrollmentLabel")}
              </div>
              <div className="counts -enroll-count">
                {formatNumber(state.totalEnrollments)}
              </div>
              <div className="sub-text -enroll">
                {translate("app.sse.studentUnduplicatedLabel")}
              </div>
            </div>
            <div className="avg-predicted-risk wm-grid-col-3 wm-grid-col-lg-3 wm-grid-col-md-5 wm-grid-col-sm-8">
              <div className="titles flex-row -verticallycentered">
                <div>{translate("app.sse.predictedRiskLabel")}</div>
                <div className="toggle-tip">
                  <WmToggletip
                    label={avgPredictedRiskInfoLabel}
                    tooltip={predictedRiskTooltipText}
                    tooltipPosition="top-right"
                  ></WmToggletip>
                </div>
              </div>
              <div className="flex-row">
                <div className="course-completion">
                  <div className="counts">{state.averageCourseRisk}</div>
                  <div className="sub-text">
                    {translate("app.sse.couseCompletionLabel")}
                  </div>
                </div>
                <div className="term-persistent">
                  <div className="counts">{state.averageTermRisk}</div>
                  <div className="sub-text">
                    {translate("app.sse.termPersistentLabel")}
                  </div>
                </div>
              </div>
            </div>
            <div className="student-outreach wm-grid-col-4 wm-grid-col-lg-4 wm-grid-col-md-5 wm-grid-col-sm-8">
              <div className="titles flex-row -verticallycentered">
                <div>{translate("app.sse.studentOutreachLabel")}</div>
                <div className="toggle-tip">
                  <WmToggletip
                    label={studentOutreachInfoLabel}
                    tooltip={studetOutreachTooltipText}
                    tooltipPosition="top-right"
                  ></WmToggletip>
                </div>
              </div>
              <div className="counts">
                {formatNumber(state.engaged)}/
                {formatNumber(state.totalEnrollments)}
              </div>
              <div className="sub-text -outreach">
                {translate("app.sse.keyEngagementLabel")}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const header = useIntl().formatMessage({
    id: "app.sse.studentInsightsLabel"
  });

  function exportToPdf() {
    let sseHtml = document.getElementById("sse-html");
    let downloadText = document.querySelector(".download-text");
    if (downloadText) {
      downloadText.setAttribute("tabindex", "0");
      (downloadText as HTMLElement).focus();
    }
    if (sseHtml) {
      let clone = sseHtml.cloneNode(true);
      let wrapperMain = document.createElement("main");
      wrapperMain.classList.add("main-container");
      wrapperMain.appendChild(clone);
      let wrapperDiv = document.createElement("div");
      wrapperDiv.appendChild(wrapperMain);
      let formData = new FormData();
      formData.append("html", wrapperDiv.outerHTML);
      formData.append("sub_header", encodeURIComponent(header));
      formData.append("header", "");
      formData.append("export_id", "student-insights-export")
      formData.append("_csrf_token", state.token ? state.token : "");

      const applyResult = (result: ExportResultSet) => {
        downloadPDF(result.pdf_file_bytes, "Student_Insights");
        dispatch({
          ...defaultSseTermSummaryAction,
          type: "setToken",
          token: null
        });
        dispatch({
          ...defaultSseTermSummaryAction,
          type: "setExportInProgress",
          isExportInProgress: false
        });
      };
      const onError = () => {
        dispatch({
          ...defaultSseTermSummaryAction,
          type: "setToken",
          token: null
        });
        dispatch({
          ...defaultSseTermSummaryAction,
          type: "setExportInProgress",
          isExportInProgress: false
        });
      };
      sendRequestWithPayload(
        {
          uri: "/export",
          body: formData
        },
        applyResult,
        onError
      );
    }
  }

  return state.showSseWidget ? (
    <section
      className="body wm-grid tab-body-home"
      id="sse-widget"
    >
      <div>
        <section className="insights">
          <div className="institution-name -bottom flex-row -justified -verticallycentered sse-desc ">
            <h3 className="helper-label helper-default-margin">{header}</h3>

            {state.terms.length > 0 && (
              <div className="flex-row download-term-container">
                {!state.termSummaryFailed && (
                  <div className="flex-row sse-export-button">
                    {state.isExportInProgress && (
                      <span className="download-text">
                        {translate(
                          "app.planParticipation.exportPdf.downloadReportText"
                        )}
                      </span>
                    )}
                    <div className="button-container">
                      <WmButton
                        button-type="secondary"
                        icon="f1da"
                        icon-size="14px"
                        onClick={() =>
                          dispatch({
                            ...defaultSseTermSummaryAction,
                            type: "setExportInProgress",
                            isExportInProgress: true
                          })
                        }
                        disabled={state.isExportInProgress}
                      >
                        {translate("app.planParticipation.exportPdf.download")}
                      </WmButton>
                    </div>
                  </div>
                )}
                <div className="terms-width">
                  <WmSelect
                    label={termLabel}
                    labelPosition="left"
                    id="select-terms"
                  >
                    {state.terms.map((term: Term) => {
                      return (
                        <WmOption
                          id={`terms-${term.id}`}
                          key={`terms-${term.id}`}
                          onClick={() => fetchTermSummary(term.id)}
                          selected={state.selectedTerm?.id === term.id}
                        >
                          {term.name}
                        </WmOption>
                      );
                    })}
                  </WmSelect>
                </div>
              </div>
            )}
          </div>
          {!state.termSummaryFailed && (
            <div className="flex-row sse-export-button-small-screen">
              {state.isExportInProgress && (
                <span className="download-text">
                  {translate(
                    "app.planParticipation.exportPdf.downloadReportText"
                  )}
                </span>
              )}
              <div className="button-container-small-screen">
                <WmButton
                  button-type="secondary"
                  icon="f1da"
                  icon-size="14px"
                  onClick={() =>
                    dispatch({
                      ...defaultSseTermSummaryAction,
                      type: "setExportInProgress",
                      isExportInProgress: true
                    })
                  }
                  disabled={state.isExportInProgress}
                >
                  {translate("app.planParticipation.exportPdf.download")}
                </WmButton>
              </div>
            </div>
          )}
          {state.terms.length > 0 && (
            <div id="sse-html">
              <div className="wm-grid-row">
                <div className="grid-container wm-grid-col-12 wm-grid-col-md-10">
                  <div className="paper-card paper-card-height">
                    <div className="header">
                      <div className="info">
                        <h3 className="title">
                          {translate("app.sse.termOverviewLabel")}
                        </h3>
                        <p className="description -des">
                          {translate("app.sse.termOverviewHelperText")}
                        </p>
                      </div>
                    </div>
                    <span
                      className="sr-only sse-loader-status"
                      role="status"
                      aria-live="assertive"
                    ></span>
                    {state.loader && (
                      <WidgetLoader
                        loaderContainerClasses="loader-container"
                        loaderClasses="widget-loader"
                      />
                    )}
                    {!state.loader && !state.termSummaryFailed && (
                      <TeamOverviewBody />
                    )}
                    {state.termSummaryFailed && <ApiError />}
                  </div>
                </div>
              </div>
              <div
                id="course-completion-risk-main"
                className="wm-grid-row course-completion-risk-main -bottoms"
              >
                <CourseCompletionRisk
                  highRiskCourseEngagement={state.highRiskCourseEngagement}
                  lowRiskCourseEngagement={state.lowRiskCourseEngagement}
                  mediumRiskCourseEngagement={state.mediumRiskCourseEngagement}
                  notIndicatedRiskCourseEngagement={
                    state.notIndicatedRiskCourseEngagement
                  }
                  highRiskCourseCompletion={state.highRiskCourseCompletion}
                  lowRiskCourseCompletion={state.lowRiskCourseCompletion}
                  mediumRiskCourseCompletion={state.mediumRiskCourseCompletion}
                  notIndicatedRiskCourseCompletion={
                    state.notIndicatedRiskCourseCompletion
                  }
                  loader={state.loader}
                  termSummaryFailed={state.termSummaryFailed}
                ></CourseCompletionRisk>
              </div>
              <div
                id="term-persistence-risk-main"
                className="wm-grid-row course-completion-risk-main -bottoms"
              >
                <TermPersistenceRisk
                  highRiskTermPersistence={state.highRiskTermPersistence}
                  mediumRiskTermPersistence={state.mediumRiskTermPersistence}
                  lowRiskTermPersistence={state.lowRiskTermPersistence}
                  notIndicatedRiskTermPersistence={
                    state.notIndicatedRiskTermPersistence
                  }
                  highRiskTermPersistenceEngagement={
                    state.highRiskTermPersistenceEngagement
                  }
                  lowRiskTermPersistenceEngagement={
                    state.lowRiskTermPersistenceEngagement
                  }
                  mediumRiskTermPersistenceEngagement={
                    state.mediumRiskTermPersistenceEngagement
                  }
                  notIndicatedRiskTermPersistenceEngagement={
                    state.notIndicatedRiskTermPersistenceEngagement
                  }
                  loader={state.loader}
                  termSummaryFailed={state.termSummaryFailed}
                ></TermPersistenceRisk>
              </div>
            </div>
          )}
        </section>
      </div>
    </section>
  ) : (
    <div></div>
  );
}
